import { getChromeProfileEmail } from '@shared/extension/utils/chrome-profile';
import { getActiveTab, isValidTab } from '@shared/extension/utils/tabs';
import { Dict, Mixpanel, init } from 'mixpanel-browser';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';
import { AnalyticsEvent } from '../enums';

interface MixpanelWithRequestsQueue {
  __request_queue?: Array<[string, EventData]>;
}

interface EventData {
  data: string;
}

interface EventBody {
  event: string;
  properties: Dict;
}

enum OS {
  Windows = 'Windows',
  MacOS = 'Mac OS X',
  Linux = 'Linux',
  ChromeOS = 'Chrome OS',
}

const OS_REGEX = {
  Windows: /Windows/i,
  Phone: /Phone/,
  WPDesktop: /WPDesktop/,
  Mac: /Mac/i,
  Linux: /Linux/,
  CrOS: /CrOS/,
};

const CHROME_BROWSER = 'Chrome';
const CHROME_VERSION_REGEXP = /Chrome\/(\d+(\.\d+)?)/;
const EMPTY_URL = 'about:blank';
const MIXPANEL_API_URL = 'https://api.mixpanel.com';
const MIXPANEL_CLIENT_NAME = 'mixpanel';

export class AnalyticsService {
  enabled: boolean = false;

  private client: Mixpanel & MixpanelWithRequestsQueue;
  private initialized$ = new BehaviorSubject(false);
  private userId: string | null;
  private version: string;

  initialize(token: string, version: string): void {
    if (token) {
      this.client = init(token, {}, MIXPANEL_CLIENT_NAME);
      this.version = version;
      this.initialized$.next(true);
    }
  }

  async identifyUser(userId: string): Promise<void> {
    if (!this.enabled) {
      return;
    }
    await firstValueFrom(
      this.initialized$.pipe(
        filter(initialized => initialized),
        take(1)
      )
    );
    this.client.identify(userId);
    this.userId = userId;
  }

  async resetUser(): Promise<void> {
    if (!this.enabled) {
      return;
    }
    await firstValueFrom(
      this.initialized$.pipe(
        filter(initialized => initialized),
        take(1)
      )
    );
    this.client.reset();
    this.userId = null;
  }

  async track(eventName: AnalyticsEvent, properties?: Dict): Promise<void> {
    if (!this.enabled) {
      return;
    }
    await firstValueFrom(
      this.initialized$.pipe(
        filter(initialized => initialized),
        take(1)
      )
    );

    console.log(`Tracking analytics event: ${eventName}, ${properties ? JSON.stringify(properties) : 'no properties'}`);

    if (eventName === AnalyticsEvent.SignIn && properties && properties['userId']) {
      this.identifyUser(properties['userId']);
    }
    const chromeProfileEmail: string | undefined = (await getChromeProfileEmail()) || undefined;
    const eventProperties: Dict = this.convertPropsFormat({
      appName: 'Extension', 
      version: this.version,
      chromeProfileEmail,
      ...properties,
    });
    try {
      this.client.track(eventName, eventProperties);
      this.handleQueue(eventName, eventProperties);
    } catch (ex: unknown) {
      if (ex) {
        console.error(`Error tracking analytics event "${eventName}": ${ex}`);
      }
    }
  }

  private async handleQueue(eventName: string, properties: Dict): Promise<void> {
    if (!this.client.__request_queue || this.client.__request_queue.length === 0) {
      return;
    }
    const requests: Array<[string, EventData]> = [...this.client.__request_queue];
    this.client.__request_queue = [];
    const activeTab: chrome.tabs.Tab | null = await getActiveTab();
    let currentUrl: string;
    if (!activeTab || !isValidTab(activeTab) || !activeTab.url) {
      currentUrl = EMPTY_URL;
    } else {
      currentUrl = activeTab.url;
    }

    for (const request of requests) {
      const body: EventData = request[1];
      const data: EventBody = JSON.parse(body.data);

      const eventBody: EventBody = {
        event: eventName,
        properties: {
          $current_url: currentUrl,
          $device_id: data.properties['$device_id'],
          $insert_id: data.properties['$insert_id'],
          $os: this.getOS(),
          $browser: this.getBrowser(),
          $browser_version: this.getBrowserVersion(),
          distinct_id: this.userId || data.properties['distinct_id'],
          time: data.properties['time'],
          token: data.properties['token'],
          ...properties,
        },
      }

      const options = {
        method: 'POST',
        headers: { accept: 'text/plain', 'content-type': 'application/json' },
        body: JSON.stringify([eventBody]),
      };

      try {
        await fetch(`${MIXPANEL_API_URL}/track?ip=1`, options);
      } catch (ex) {
        console.error(`Error handling queued analytics event "${eventName}": ${ex}`);
      }
    }
  }

  private convertPropsFormat(properties: Dict): Dict {
    const convertedProps: Dict = {};
    for (const key in properties) {
      if (properties.hasOwnProperty(key) && properties[key] !== null) {
        const newKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        convertedProps[newKey] = properties[key];
      }
    }
    return convertedProps;
  }

  private getBrowser(): string | undefined {
    if (!navigator || !navigator.userAgent) {
      return;
    }
    const userAgent: string = navigator.userAgent;
    if (this.includes(userAgent, CHROME_BROWSER)) {
      return CHROME_BROWSER;
    } else {
      return;
    }
  }

  private getBrowserVersion(): number | undefined {
    if (!navigator || !navigator.userAgent) {
      return;
    }
    const browser: string | undefined = this.getBrowser();
    if (!browser || browser !== CHROME_BROWSER) {
      return;
    }
    const matches: RegExpMatchArray = navigator.userAgent.match(CHROME_VERSION_REGEXP);
    if (!matches) {
      return;
    }
    return parseFloat(matches[matches.length - 2]);
  }

  private getOS(): string | undefined {
    if (!navigator || !navigator.userAgent) {
      return;
    }
    const userAgent: string = navigator.userAgent;
  
    if (OS_REGEX.Windows.test(userAgent) && !OS_REGEX.Phone.test(userAgent) && !OS_REGEX.WPDesktop.test(userAgent)) {
      return OS.Windows;
    }
    if (OS_REGEX.Mac.test(userAgent)) {
      return OS.MacOS;
    } else if (OS_REGEX.Linux.test(userAgent)) {
      return OS.Linux;
    } else if (OS_REGEX.CrOS.test(userAgent)) {
      return OS.ChromeOS;
    }
  }

  private includes(str: string, needle: string): boolean {
    return str.indexOf(needle) !== -1;
  }
}
